import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses, formatMoney } from "../theme";

/**
 * Costo del equipo con varias monedas (#741).
 *
 * La plataforma no tiene tipo de cambio, así que pesos y dólares NO se suman:
 * si el desglose trae más de una moneda se muestran los dos importes, uno por
 * moneda. La cifra única sólo se usa cuando hay una sola moneda, que es el
 * único caso en que significa algo.
 */
function costLabel(
  byCurrency: Record<string, number> | undefined,
  fallback: number | undefined,
): string {
  const entries = Object.entries(byCurrency ?? {});
  if (entries.length === 0) return formatMoney(fallback);
  return entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, amount]) => formatMoney(amount, currency))
    .join(" · ");
}
import { fetchSummary } from "../services/teamService";
import type { SummaryTeamMembers } from "../types";

// Resumen de la mini-app: plantilla, squads, costo del equipo y de
// contratistas, e imputación del costo por proyecto/centro de costo (AgroCloud#13/#60).
// Read-only.
export default function SummarySection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [data, setData] = useState<SummaryTeamMembers | null>(null);
  const [loading, setLoading] = useState(true);
  // Si la carga falla hay que DECIRLO. Dejar los indicadores en cero es
  // indistinguible de una empresa sin plantilla: el usuario cree que no tiene
  // colaboradores cuando en realidad no se pudo leer el resumen.
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const summary = await fetchSummary();
        if (alive) {
          setData(summary);
          setError(null);
        }
      } catch {
        if (alive) {
          setData(null);
          setError(t("team.loadError"));
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [t]);

  const cards = [
    {
      label: t("team.summary.teamMembers"),
      value: String(data?.teamMembers ?? 0),
      icon: "ri-team-line",
      tone: "text-primary-500",
    },
    {
      label: t("team.summary.active"),
      value: String(data?.teamMembersActive ?? 0),
      icon: "ri-user-follow-line",
      tone: "text-emerald-500",
    },
    {
      label: t("team.summary.squads"),
      value: String(data?.squads ?? 0),
      icon: "ri-group-2-line",
      tone: "text-sky-500",
    },
    {
      label: t("team.summary.rates"),
      value: String(data?.rates ?? 0),
      icon: "ri-price-tag-3-line",
      tone: "text-amber-500",
    },
    {
      label: t("team.summary.laborCost"),
      value: costLabel(data?.laborCostByCurrency, data?.laborCost),
      icon: "ri-money-dollar-circle-line",
      tone: "text-purple-500",
    },
    {
      label: t("team.summary.contractorCost"),
      value: costLabel(data?.contractorCostByCurrency, data?.contractorCost),
      icon: "ri-briefcase-line",
      tone: "text-orange-500",
    },
  ];

  // Imputación del costo del equipo por proyecto (criterio AgroCloud#13).
  const byProject = Object.entries(data?.costByProject ?? {});

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
            <i className="ri-dashboard-line text-xl" />
          </span>
          <div>
            <h1 className={`text-xl font-bold ${tc.text}`}>{t("team.menu.summary")}</h1>
            <p className={`text-sm ${tc.textSecondary}`}>{t("team.summarySubtitle")}</p>
          </div>
        </div>

        {/* Mismo banner de error que el resto de las secciones del módulo. */}
        {error && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-500">
            <i className="ri-error-warning-line mr-1" />
            {error}
          </div>
        )}

        {loading ? (
          <div
            className={`rounded-2xl border p-8 text-center ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}
          >
            <i className="ri-loader-4-line animate-spin mr-1" />
            {t("team.loading")}
          </div>
        ) : error ? null : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cards.map((c) => (
                <div key={c.label} className={`rounded-2xl border p-5 ${tc.cardBorder} ${tc.cardBg}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${tc.textSecondary}`}>{c.label}</span>
                    <i className={`${c.icon} text-xl ${c.tone}`} />
                  </div>
                  <p className={`mt-2 text-2xl font-bold ${tc.text}`}>{c.value}</p>
                </div>
              ))}
            </div>

            {byProject.length > 0 && (
              <div className={`mt-6 rounded-2xl border p-5 ${tc.cardBorder} ${tc.cardBg}`}>
                <h2 className={`mb-3 text-sm font-semibold ${tc.text}`}>
                  <i className="ri-pie-chart-line mr-1 text-primary-500" />
                  {t("team.summary.costByProject")}
                </h2>
                <dl className="space-y-2">
                  {byProject.map(([project, amount]) => (
                    <div key={project} className="flex justify-between gap-2 text-sm">
                      <dt className={tc.textSecondary}>
                        {project === "unallocated" ? t("team.unallocated") : project}
                      </dt>
                      <dd className={tc.text}>{formatMoney(amount)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
